import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { commonErrorResponses, payloadEnvelope, requiredPayloadEnvelope } from '../envelope.js';
/** admin 약관 CMS의 문서 한 행이다. */
const termDocumentAdminSchema = z.object({
    /** TermDocument 행 id다. */
    id: z.string(),
    /** 약관 종류 키다. 예: `terms_of_service`, `privacy_policy`. */
    type: z.string(),
    /** 같은 type 안에서 증가하는 버전 번호다. */
    version: z.number(),
    /** 문서 제목이다. */
    title: z.string(),
    /** 목록·카드용 요약이다. */
    summary: z.string(),
    /** 본문 HTML 또는 마크다운 원문이다. */
    body: z.string(),
    /** `draft`는 초안, `published`는 게시된 버전이다. */
    status: z.enum(['draft', 'published']),
    /** true면 게시 후 기존 동의자에게 재동의를 요구한다. */
    requiresReconsent: z.boolean(),
    /** 최초 게시 시각이다. 초안이면 null이다. */
    publishedAt: z.union([z.string(), z.null()]),
    /** 효력 발생 시각이다. 미정이면 null이다. */
    effectiveAt: z.union([z.string(), z.null()]),
    /** 행 생성 시각이다. */
    createdAt: z.union([z.string(), z.date()]),
    /** 행 마지막 수정 시각이다. */
    updatedAt: z.union([z.string(), z.date()]),
});
const c = initContract();
/** admin-api — 약관 문서 초안·수정·게시·삭제 CMS. */
export const adminTermsContract = c.router({
    /** 약관 문서 목록을 조회한다. type·status로 필터할 수 있다. */
    list: {
        method: 'GET',
        path: '/terms',
        query: z.object({
            /** 약관 종류로 좁힌다. */
            type: z.string().optional(),
            /** `draft` 또는 `published`로 좁힌다. */
            status: z.string().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.array(termDocumentAdminSchema)),
            ...commonErrorResponses,
        },
    },
    /** 새 약관 초안을 만든다. */
    create: {
        method: 'POST',
        path: '/terms',
        body: z.object({
            /** 약관 종류 키다. */
            type: z.string().optional(),
            /** 문서 제목이다. */
            title: z.string().optional(),
            /** 목록용 요약이다. */
            summary: z.string().optional(),
            /** 본문 원문이다. */
            body: z.string().optional(),
            /** 효력 발생 시각(ISO 문자열)이다. null이면 미정이다. */
            effectiveAt: z.string().nullable().optional(),
            /** 게시 후 재동의를 요구할지 여부다. */
            requiresReconsent: z.boolean().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(termDocumentAdminSchema),
            ...commonErrorResponses,
        },
    },
    /** 초안 문서의 메타·본문을 수정한다. */
    update: {
        method: 'PATCH',
        path: '/terms/:id',
        pathParams: z.object({
            /** 수정할 TermDocument id다. */
            id: z.string(),
        }),
        body: z.object({
            /** 문서 제목이다. */
            title: z.string().optional(),
            /** 목록용 요약이다. */
            summary: z.string().optional(),
            /** 본문 원문이다. */
            body: z.string().optional(),
            /** 효력 발생 시각(ISO 문자열)이다. null이면 미정이다. */
            effectiveAt: z.string().nullable().optional(),
            /** 게시 후 재동의를 요구할지 여부다. */
            requiresReconsent: z.boolean().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(termDocumentAdminSchema),
            ...commonErrorResponses,
        },
    },
    /** 초안을 게시한다. version이 확정되고 publishedAt이 채워진다. */
    publish: {
        method: 'POST',
        path: '/terms/:id/publish',
        pathParams: z.object({
            /** 게시할 TermDocument id다. */
            id: z.string(),
        }),
        body: c.noBody(),
        responses: {
            200: requiredPayloadEnvelope(termDocumentAdminSchema),
            ...commonErrorResponses,
        },
    },
    /** 초안 문서를 삭제한다. 게시된 문서는 이 경로로 지우지 않는다. */
    remove: {
        method: 'DELETE',
        path: '/terms/:id',
        pathParams: z.object({
            /** 삭제할 TermDocument id다. */
            id: z.string(),
        }),
        responses: {
            200: payloadEnvelope(z.null()),
            ...commonErrorResponses,
        },
    },
});
export { termDocumentAdminSchema };
//# sourceMappingURL=terms.contract.js.map